import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useLanguage } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import { ArrowLeft, ArrowRight, Clock, Loader2 } from "lucide-react";

const BlogDetail = () => {
  const { slug } = useParams();
  const { t, locale } = useLanguage();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api
      .get(`/blog/posts/${slug}`)
      .then(({ data }) => setPost(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  return (
    <div
      data-testid="blog-detail-page"
      className="min-h-screen bg-[#fdfbf7] text-neutral-900"
    >
      <Navigation />

      <main className="pt-32 lg:pt-40">
        <article className="container-px mx-auto max-w-3xl">
          <Link
            to="/blog"
            data-testid="blog-detail-back"
            className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("blog.back")}
          </Link>

          {notFound ? (
            <div className="py-24 text-center text-neutral-500">404 · {t("blog.empty")}</div>
          ) : !post ? (
            <div className="py-24 grid place-items-center">
              <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
            </div>
          ) : (
            <>
              <div className="mt-8 flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-sky-700">
                {(post.tags || []).slice(0, 3).map((tag) => (
                  <span key={tag}>{tag}</span>
                ))}
                <span className="inline-flex items-center gap-1 text-neutral-400 normal-case tracking-normal">
                  <Clock className="w-3 h-3" /> {post.read_minutes} {t("blog.minutes")}
                </span>
              </div>

              <h1 className="font-display mt-4 text-4xl lg:text-6xl font-light tracking-tighter text-neutral-950 text-balance">
                {post.title[locale] || post.title.de}
              </h1>

              <p className="mt-5 text-lg text-neutral-700 leading-relaxed">
                {post.excerpt[locale] || post.excerpt.de}
              </p>

              {post.cover_image && (
                <div className="mt-10 rounded-3xl overflow-hidden border border-neutral-200">
                  <img
                    src={post.cover_image}
                    alt={post.title[locale] || post.title.de}
                    className="w-full h-auto"
                  />
                </div>
              )}

              <div
                data-testid="blog-detail-content"
                className="prose prose-neutral mt-12 max-w-none
                  prose-headings:font-display prose-headings:font-medium prose-headings:tracking-tight
                  prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4
                  prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                  prose-p:text-[17px] prose-p:leading-relaxed prose-p:text-neutral-800
                  prose-li:text-[17px] prose-li:text-neutral-800
                  prose-strong:text-neutral-950
                  prose-a:text-sky-700 prose-a:no-underline hover:prose-a:underline
                  prose-table:my-8 prose-th:bg-neutral-50 prose-th:font-medium prose-th:text-neutral-700
                  prose-blockquote:border-l-sky-500 prose-blockquote:bg-sky-50/60 prose-blockquote:rounded-r-2xl prose-blockquote:px-6 prose-blockquote:py-1 prose-blockquote:not-italic prose-blockquote:text-neutral-800"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {post.content[locale] || post.content.de}
                </ReactMarkdown>
              </div>

              <div className="mt-16 rounded-3xl bg-neutral-950 text-white p-8 lg:p-10">
                <h3 className="font-display text-2xl lg:text-3xl font-light tracking-tight">
                  {t("blog.cta.title")}
                </h3>
                <p className="mt-3 text-neutral-300">{t("blog.cta.subtitle")}</p>
                <Link
                  to="/#contact"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-neutral-950 px-6 py-3.5 text-sm font-medium hover:bg-sky-50 transition-colors"
                >
                  {t("nav.cta")}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </>
          )}
        </article>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default BlogDetail;
